import React, { useState, useEffect } from 'react';
import APP_COLOURS from '../../config/colors';
import { managerAPI } from '../../config/api';
import { ThemeSegmented } from '../ThemeToggle';

const ManagerProfile = ({ userData, onClose, onUpdateSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    first_name: userData?.first_name || '',
    last_name: userData?.last_name || '',
    email: userData?.email || '',
    cell: userData?.cell || '',
    position: userData?.position || '',
    dob: userData?.dob || '',
    gender: userData?.gender || '',
    id_passport: userData?.id_passport || '',
  });

  const [notifications, setNotifications] = useState({
    emailEnabled: userData?.emailNotifications?.enabled || false,
    dailyReport: userData?.emailNotifications?.dailyReport || false,
    weeklyReport: userData?.emailNotifications?.weeklyReport || false,
    staffUpdates: userData?.emailNotifications?.staffUpdates || false,
    salesAlerts: userData?.emailNotifications?.salesAlerts || false,
    attendanceAlerts: userData?.emailNotifications?.attendanceAlerts || false,
  });

  const [passwordData, setPasswordData] = useState({
    currentPasscode: '',
    newPasscode: '',
    confirmPasscode: '',
  });

  const [showPasswordChange, setShowPasswordChange] = useState(false);

  useEffect(() => {
    // Reset success message after 3 seconds
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setNotifications(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setError('');
      
      const updateData = {
        ...formData,
        emailNotifications: notifications
      };

      await managerAPI.updateProfile(updateData);
      
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      
      // Update local storage with new data
      const storedUserData = JSON.parse(localStorage.getItem('traq_user_data') || '{}');
      const updatedUserData = {
        ...storedUserData,
        ...formData,
        emailNotifications: notifications
      };
      localStorage.setItem('traq_user_data', JSON.stringify(updatedUserData));
      
      if (onUpdateSuccess) {
        onUpdateSuccess(updatedUserData);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      if (passwordData.newPasscode !== passwordData.confirmPasscode) {
        setError('New passcodes do not match');
        return;
      }

      if (passwordData.newPasscode.length < 5) {
        setError('New passcode must be at least 5 characters');
        return;
      }

      setLoading(true);
      setError('');

      await managerAPI.updatePassword({
        currentPasscode: passwordData.currentPasscode,
        newPasscode: passwordData.newPasscode
      });

      setSuccess('Password changed successfully!');
      setShowPasswordChange(false);
      setPasswordData({
        currentPasscode: '',
        newPasscode: '',
        confirmPasscode: '',
      });
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const InputField = ({ label, name, type = 'text', value, disabled, required }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
        {label} {required && <span style={{ color: APP_COLOURS.ERROR }}>*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={handleInputChange}
        disabled={!isEditing || disabled}
        className="w-full px-4 py-2 rounded-lg border outline-none transition-all duration-200"
        style={{
          backgroundColor: isEditing && !disabled ? APP_COLOURS.BG : `${APP_COLOURS.PLACEHOLDER}20`,
          borderColor: `${APP_COLOURS.BLUE}40`,
          color: APP_COLOURS.TEXTCOLOR,
        }}
      />
    </div>
  );

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{ backgroundColor: APP_COLOURS.CARD }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between"
          style={{
            backgroundColor: APP_COLOURS.CARD,
            borderColor: `${APP_COLOURS.BLUE}40`,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
              style={{
                background: `linear-gradient(135deg, ${APP_COLOURS.BLUE}, ${APP_COLOURS.PURPLE})`,
              }}
            >
              {userData?.first_name?.[0]}{userData?.last_name?.[0]}
            </div>
            <div>
              <h2 className="text-2xl font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                Manager Profile
              </h2>
              <p className="text-sm opacity-70" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                View and manage your account settings
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:rotate-90"
            style={{
              backgroundColor: `${APP_COLOURS.ERROR}20`,
              color: APP_COLOURS.ERROR,
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 rounded-lg flex items-center gap-2" style={{ backgroundColor: `${APP_COLOURS.ERROR}20`, color: APP_COLOURS.ERROR }}>
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-4 rounded-lg flex items-center gap-2" style={{ backgroundColor: `${APP_COLOURS.SUCCESS}20`, color: APP_COLOURS.SUCCESS }}>
              <i className="fas fa-check-circle"></i>
              <span>{success}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mb-6">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{
                  backgroundColor: APP_COLOURS.BLUE,
                  color: 'white',
                }}
              >
                <i className="fas fa-edit mr-2"></i>
                Edit Profile
              </button>
            ) : (
              <>
                <button
                  onClick={handleSaveProfile}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  style={{
                    backgroundColor: APP_COLOURS.SUCCESS,
                    color: 'white',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  <i className="fas fa-save mr-2"></i>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({
                      first_name: userData?.first_name || '',
                      last_name: userData?.last_name || '',
                      email: userData?.email || '',
                      cell: userData?.cell || '',
                      position: userData?.position || '',
                      dob: userData?.dob || '',
                      gender: userData?.gender || '',
                      id_passport: userData?.id_passport || '',
                    });
                    setError('');
                  }}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  style={{
                    backgroundColor: `${APP_COLOURS.PLACEHOLDER}40`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
              </>
            )}
          </div>

          {/* Profile Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Personal Information Section */}
            <div
              className="p-4 rounded-lg border"
              style={{ borderColor: `${APP_COLOURS.BLUE}40` }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                Personal Information
              </h3>
              <InputField label="First Name" name="first_name" value={formData.first_name} required />
              <InputField label="Last Name" name="last_name" value={formData.last_name} required />
              <InputField label="Email" name="email" type="email" value={formData.email} disabled />
              <InputField label="Phone Number" name="cell" type="tel" value={formData.cell} />
            </div>

            {/* Additional Details Section */}
            <div
              className="p-4 rounded-lg border"
              style={{ borderColor: `${APP_COLOURS.BLUE}40` }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                Additional Details
              </h3>
              <InputField label="Position" name="position" value={formData.position} />
              <InputField label="Date of Birth" name="dob" type="date" value={formData.dob} />
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-2 rounded-lg border outline-none transition-all duration-200"
                  style={{
                    backgroundColor: isEditing ? APP_COLOURS.BG : `${APP_COLOURS.PLACEHOLDER}20`,
                    borderColor: `${APP_COLOURS.BLUE}40`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>

              <InputField label="ID/Passport Number" name="id_passport" value={formData.id_passport} />
            </div>
          </div>

          {/* Organisation Info (Read-only) */}
          <div
            className="p-4 rounded-lg border mb-6"
            style={{ borderColor: `${APP_COLOURS.BLUE}40`, backgroundColor: `${APP_COLOURS.PLACEHOLDER}10` }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              Organisation Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm opacity-70 mb-1" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Organisation ID
                </p>
                <p className="font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  {userData?.organisationID || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm opacity-70 mb-1" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Permission Level
                </p>
                <span
                  className="inline-block px-3 py-1 rounded-full text-sm font-medium"
                  style={{
                    backgroundColor: `${APP_COLOURS.BLUE}20`,
                    color: APP_COLOURS.BLUE,
                  }}
                >
                  {userData?.permission || 'Manager'}
                </span>
              </div>
            </div>
            {userData?.brands && userData.brands.length > 0 && (
              <div className="mt-4">
                <p className="text-sm opacity-70 mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Assigned Brands
                </p>
                <div className="flex flex-wrap gap-2">
                  {userData.brands.map((brand, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm"
                      style={{
                        backgroundColor: `${APP_COLOURS.PURPLE}20`,
                        color: APP_COLOURS.PURPLE,
                      }}
                    >
                      {brand}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Appearance */}
          <div
            className="p-4 rounded-lg border mb-6"
            style={{ borderColor: `${APP_COLOURS.BLUE}40` }}
          >
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              <i className="fas fa-circle-half-stroke"></i>
              Appearance
            </h3>
            <p className="text-sm mb-4" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              Light, dark, or match this device
            </p>
            <ThemeSegmented />
          </div>

          {/* Email Notifications */}
          <div
            className="p-4 rounded-lg border mb-6"
            style={{ borderColor: `${APP_COLOURS.BLUE}40` }}
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              <i className="fas fa-bell"></i>
              Email Notifications
            </h3>
            
            <div className="mb-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="emailEnabled"
                  checked={notifications.emailEnabled}
                  onChange={handleNotificationChange}
                  disabled={!isEditing}
                  className="w-5 h-5 rounded"
                  style={{ accentColor: APP_COLOURS.BLUE }}
                />
                <div>
                  <p className="font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    Enable Email Notifications
                  </p>
                  <p className="text-sm opacity-70" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    Receive notifications about your team's activities
                  </p>
                </div>
              </label>
            </div>

            {notifications.emailEnabled && (
              <div className="ml-8 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="dailyReport"
                    checked={notifications.dailyReport}
                    onChange={handleNotificationChange}
                    disabled={!isEditing}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: APP_COLOURS.BLUE }}
                  />
                  <span style={{ color: APP_COLOURS.TEXTCOLOR }}>Daily Activity Report</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="weeklyReport"
                    checked={notifications.weeklyReport}
                    onChange={handleNotificationChange}
                    disabled={!isEditing}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: APP_COLOURS.BLUE }}
                  />
                  <span style={{ color: APP_COLOURS.TEXTCOLOR }}>Weekly Summary Report</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="staffUpdates"
                    checked={notifications.staffUpdates}
                    onChange={handleNotificationChange}
                    disabled={!isEditing}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: APP_COLOURS.BLUE }}
                  />
                  <span style={{ color: APP_COLOURS.TEXTCOLOR }}>Staff Status Updates</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="salesAlerts"
                    checked={notifications.salesAlerts}
                    onChange={handleNotificationChange}
                    disabled={!isEditing}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: APP_COLOURS.BLUE }}
                  />
                  <span style={{ color: APP_COLOURS.TEXTCOLOR }}>Sales Alerts & Milestones</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="attendanceAlerts"
                    checked={notifications.attendanceAlerts}
                    onChange={handleNotificationChange}
                    disabled={!isEditing}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: APP_COLOURS.BLUE }}
                  />
                  <span style={{ color: APP_COLOURS.TEXTCOLOR }}>Attendance Alerts</span>
                </label>
              </div>
            )}
          </div>

          {/* Password Change Section */}
          <div
            className="p-4 rounded-lg border"
            style={{ borderColor: `${APP_COLOURS.BLUE}40` }}
          >
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              <i className="fas fa-lock"></i>
              Security
            </h3>
            
            {!showPasswordChange ? (
              <button
                onClick={() => setShowPasswordChange(true)}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{
                  backgroundColor: `${APP_COLOURS.BLUE}20`,
                  color: APP_COLOURS.BLUE,
                }}
              >
                <i className="fas fa-key mr-2"></i>
                Change Password
              </button>
            ) : (
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    Current Passcode *
                  </label>
                  <input
                    type="password"
                    name="currentPasscode"
                    value={passwordData.currentPasscode}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-2 rounded-lg border outline-none"
                    style={{
                      backgroundColor: APP_COLOURS.BG,
                      borderColor: `${APP_COLOURS.BLUE}40`,
                      color: APP_COLOURS.TEXTCOLOR,
                    }}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    New Passcode * (min. 5 characters)
                  </label>
                  <input
                    type="password"
                    name="newPasscode"
                    value={passwordData.newPasscode}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-2 rounded-lg border outline-none"
                    style={{
                      backgroundColor: APP_COLOURS.BG,
                      borderColor: `${APP_COLOURS.BLUE}40`,
                      color: APP_COLOURS.TEXTCOLOR,
                    }}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    Confirm New Passcode *
                  </label>
                  <input
                    type="password"
                    name="confirmPasscode"
                    value={passwordData.confirmPasscode}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-2 rounded-lg border outline-none"
                    style={{
                      backgroundColor: APP_COLOURS.BG,
                      borderColor: `${APP_COLOURS.BLUE}40`,
                      color: APP_COLOURS.TEXTCOLOR,
                    }}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleChangePassword}
                    disabled={loading || !passwordData.currentPasscode || !passwordData.newPasscode || !passwordData.confirmPasscode}
                    className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                    style={{
                      backgroundColor: APP_COLOURS.SUCCESS,
                      color: 'white',
                      opacity: loading || !passwordData.currentPasscode || !passwordData.newPasscode || !passwordData.confirmPasscode ? 0.5 : 1,
                    }}
                  >
                    <i className="fas fa-check mr-2"></i>
                    {loading ? 'Updating...' : 'Update Password'}
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordChange(false);
                      setPasswordData({
                        currentPasscode: '',
                        newPasscode: '',
                        confirmPasscode: '',
                      });
                      setError('');
                    }}
                    disabled={loading}
                    className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                    style={{
                      backgroundColor: `${APP_COLOURS.PLACEHOLDER}40`,
                      color: APP_COLOURS.TEXTCOLOR,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerProfile;

